import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  min-width: 500px;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Content = styled.div`
  padding: 1.5rem;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-gray-900);
`;

const ReasonOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--border-radius);
  cursor: pointer;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-gray-50);
    border-color: var(--color-gray-300);
  }

  &:has(input:checked) {
    background: var(--color-orange-50);
    border-color: var(--color-orange-500);
  }
`;

const Radio = styled.input`
  margin-top: 0.125rem;
`;

const ReasonText = styled.div`
  flex: 1;
`;

const ReasonLabel = styled.div`
  font-weight: 500;
  color: var(--color-gray-900);
  margin-bottom: 0.25rem;
`;

const ReasonDescription = styled.div`
  font-size: 0.875rem;
  color: var(--color-gray-600);
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius);
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--color-orange-500);
    box-shadow: 0 0 0 1px var(--color-orange-500);
  }

  &:disabled {
    background: var(--color-gray-100);
    color: var(--color-gray-500);
  }
`;

const Actions = styled.div`
  padding: 1.5rem;
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Button = styled.button<{ $variant?: 'primary' | 'warning' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius);
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${props => {
    switch (props.$variant) {
      case 'warning':
        return `
          background: #F59E0B;
          border-color: #F59E0B;
          color: white;
          &:hover:not(:disabled) {
            background: #D97706;
            border-color: #D97706;
          }
        `;
      case 'primary':
        return `
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
          &:hover:not(:disabled) {
            background: var(--color-primary-dark);
            border-color: var(--color-primary-dark);
          }
        `;
      default:
        return `
          background: white;
          border-color: var(--color-gray-300);
          color: var(--color-gray-700);
          &:hover:not(:disabled) {
            background: var(--color-gray-50);
            border-color: var(--color-gray-400);
          }
        `;
    }
  }}
`;

const InfoBox = styled.div`
  background: #FEF3C7;
  border: 1px solid #F59E0B;
  border-radius: var(--border-radius);
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: #92400E;
  line-height: 1.5;
`;

const REVISION_SUGGESTIONS = [
  {
    id: 'tone_down_violence',
    label: 'Tone Down Violence',
    description: 'Story contains scenes that are too graphic or violent for the target age group'
  },
  {
    id: 'age_appropriate_themes',
    label: 'Adjust Age-Appropriate Themes',
    description: 'Some themes or concepts need to be simplified or adjusted for children ages 5-12'
  },
  {
    id: 'improve_language',
    label: 'Improve Language',
    description: 'Some words or phrases should be replaced with more child-friendly alternatives'
  },
  {
    id: 'reduce_scary_content',
    label: 'Reduce Scary Content',
    description: 'Story contains elements that might be too frightening or disturbing for young readers'
  },
  {
    id: 'improve_character_behavior',
    label: 'Improve Character Behavior',
    description: 'Characters exhibit behavior that should not be modeled (bullying, disrespect, etc.)'
  },
  {
    id: 'clarify_moral_lesson',
    label: 'Clarify Positive Message',
    description: 'Story needs clearer positive values or moral lessons appropriate for children'
  },
  {
    id: 'custom',
    label: 'Other (Custom Guidance)',
    description: 'Provide specific custom guidance for revision'
  }
];

export interface RequestRevisionModalProps {
  isOpen: boolean;
  storyTitle: string;
  onConfirm: (revisionNotes: string) => void;
  onCancel: () => void;
}

export const RequestRevisionModal: React.FC<RequestRevisionModalProps> = ({
  isOpen,
  storyTitle,
  onConfirm,
  onCancel
}) => {
  const [selectedSuggestion, setSelectedSuggestion] = useState<string>('');
  const [customNotes, setCustomNotes] = useState<string>('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    let finalNotes = '';

    if (selectedSuggestion === 'custom') {
      finalNotes = customNotes.trim();
    } else {
      const suggestion = REVISION_SUGGESTIONS.find(s => s.id === selectedSuggestion);
      if (suggestion) {
        finalNotes = `${suggestion.label}: ${suggestion.description}`;
      }
    }

    if (!finalNotes) {
      return; // Don't allow empty notes
    }

    onConfirm(finalNotes);

    // Reset form
    setSelectedSuggestion('');
    setCustomNotes('');
  };

  const handleCancel = () => {
    setSelectedSuggestion('');
    setCustomNotes('');
    onCancel();
  };

  const isFormValid = selectedSuggestion && (selectedSuggestion !== 'custom' || customNotes.trim());

  return (
    <Overlay onClick={handleCancel}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <span>✏️</span>
            Request Revision
          </Title>
        </Header>
        <Content>
          <InfoBox>
            <strong>💡 About Revision Requests</strong><br />
            Requesting a revision allows the author to make changes while keeping the story active.
            The author will receive your feedback and can resubmit for review once changes are made.
          </InfoBox>

          <Section>
            <p style={{ margin: '0 0 1rem 0', color: 'var(--color-gray-700)' }}>
              Story: <strong>"{storyTitle}"</strong>
            </p>
            <p style={{ margin: '0 0 1.5rem 0', color: 'var(--color-gray-600)', fontSize: '0.875rem' }}>
              Please select what needs to be revised. Be specific so the author understands what changes to make.
            </p>
          </Section>

          <Section>
            <SectionTitle>Revision Guidance</SectionTitle>
            {REVISION_SUGGESTIONS.map(suggestion => (
              <ReasonOption key={suggestion.id}>
                <Radio
                  type="radio"
                  name="revisionSuggestion"
                  value={suggestion.id}
                  checked={selectedSuggestion === suggestion.id}
                  onChange={(e) => setSelectedSuggestion(e.target.value)}
                />
                <ReasonText>
                  <ReasonLabel>{suggestion.label}</ReasonLabel>
                  <ReasonDescription>{suggestion.description}</ReasonDescription>
                </ReasonText>
              </ReasonOption>
            ))}
          </Section>

          {selectedSuggestion === 'custom' && (
            <Section>
              <SectionTitle>Custom Revision Notes</SectionTitle>
              <TextArea
                value={customNotes}
                onChange={(e) => setCustomNotes(e.target.value)}
                placeholder="Provide detailed guidance on what needs to be changed. Be specific and constructive..."
                maxLength={1000}
              />
              <div style={{
                textAlign: 'right',
                fontSize: '0.75rem',
                color: 'var(--color-gray-500)',
                marginTop: '0.25rem'
              }}>
                {customNotes.length}/1000 characters
              </div>
            </Section>
          )}
        </Content>
        <Actions>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
          <Button $variant="warning" onClick={handleConfirm} disabled={!isFormValid}>
            Request Revision
          </Button>
        </Actions>
      </Modal>
    </Overlay>
  );
};
